import { createSlice } from '@reduxjs/toolkit'
import { fetchUserNotifications, markNotificationAsRead } from 'configs/axios/api_helper'

const initialState = {
  items: [],
  pagination: { page: 1, limit: 20, total: 0, nextPage: null, previousPage: null },
  loading: false,
  error: null,
  unreadByCustomer: {},
  unreadTotal: 0,
  lastFetchedAt: null,
  activeCustomerFilter: null
}

const fallbackNotificationId = payload => {
  if (!payload) return `${Date.now()}-${Math.random()}`
  const base =
    payload.id ||
    payload.notificationId ||
    payload.invoiceId ||
    payload.customerId ||
    payload.eventType ||
    'notification'
  return `${base}-${payload.createdAt || Date.now()}-${Math.random()}`
}

const recomputeUnread = state => {
  const unreadMap = {}
  state.items.forEach(notification => {
    if (notification?.readByUser) return
    const key = notification.customerId || 'global'
    unreadMap[key] = (unreadMap[key] || 0) + 1
  })
  state.unreadByCustomer = unreadMap
  state.unreadTotal = Object.values(unreadMap).reduce((acc, value) => acc + value, 0)
}

const notificationsSlice = createSlice({
  name: 'notificationsCenter',
  initialState,
  reducers: {
    startNotificationsRequest: state => {
      state.loading = true
      state.error = null
    },
    notificationsRequestFailed: (state, action) => {
      state.loading = false
      state.error = action.payload || 'Unable to load notifications'
    },
    notificationsReceived: (state, action) => {
      const { items = [], pagination = {}, customerId = null } = action.payload || {}
      const withIds = items.map(item => ({
        ...item,
        id: item?.id || item?.notificationId || fallbackNotificationId(item)
      }))
      state.items = withIds.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      state.pagination = { ...state.pagination, ...pagination }
      state.activeCustomerFilter = customerId
      state.loading = false
      state.error = null
      state.lastFetchedAt = new Date().toISOString()
      recomputeUnread(state)
    },
    notificationUpserted: (state, action) => {
      const payload = action.payload
      if (!payload) return
      const notificationWithId = {
        ...payload,
        id: payload.id || payload.notificationId || fallbackNotificationId(payload)
      }
      const existingIndex = state.items.findIndex(notification => notification.id === notificationWithId.id)
      if (existingIndex !== -1) {
        state.items[existingIndex] = { ...state.items[existingIndex], ...notificationWithId }
      } else {
        state.items = [notificationWithId, ...state.items]
      }
      recomputeUnread(state)
    },
    notificationMarkedReadLocally: (state, action) => {
      const notificationId = action.payload
      const target = state.items.find(notification => notification.id === notificationId)
      if (target) {
        target.readByUser = true
        target.readAt = target.readAt || new Date().toISOString()
        recomputeUnread(state)
      }
    }
  }
})

export const {
  startNotificationsRequest,
  notificationsRequestFailed,
  notificationsReceived,
  notificationUpserted,
  notificationMarkedReadLocally
} = notificationsSlice.actions

export const fetchNotificationsCenter =
  ({ customerId, page = 1, limit = 20, status } = {}) =>
  async dispatch => {
    try {
      dispatch(startNotificationsRequest())
      const { data } = await fetchUserNotifications({ customerId, page, limit, status })
      const { data: items = [], ...pagination } = data || {}
      dispatch(
        notificationsReceived({
          items,
          pagination,
          customerId: customerId || null
        })
      )
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || 'Unable to load notifications'
      dispatch(notificationsRequestFailed(message))
    }
  }

export const acknowledgeNotification = (notificationId, email) => async dispatch => {
  if (!notificationId) return
  dispatch(notificationMarkedReadLocally(notificationId))
  try {
    await markNotificationAsRead(notificationId, email)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to mark notification as read', error)
  }
}

export const notificationsSelector = state => state.notifications
export const unreadNotificationsSelector = state => state.notifications.unreadTotal
export const unreadByCustomerSelector = state => state.notifications.unreadByCustomer

export default notificationsSlice.reducer
